import { normalizeWebAddress } from '../web-address'

import type { SetupScreen, SetupStepNavigator } from './setup-screen'

/** The brand-facts screens, in order. The index of a screen is its place in setup. */
export const BRAND_FACTS_SCREENS = [
  { id: 'links', transient: false },
  { id: 'reading', transient: true },
  { id: 'sheet', transient: false },
] as const satisfies readonly SetupScreen[]

/** One brand-facts screen. A failed read is the failed variant of `reading`. */
export type BrandFactsScreen = (typeof BRAND_FACTS_SCREENS)[number]['id']

export type BrandFactsMove =
  /** Next on the links, with the links of the saved sheet unchanged. */
  | 'keep-links'
  /** A read opened from the links or the sheet has shown up. */
  | 'read-opened'
  /** The read on show has finished and the ring has burst. */
  | 'read-landed'
  /** Try again on a failed read. */
  | 'retry'

/** The two links a read takes. */
export interface BrandFactsLinks {
  /** The website address. */
  readonly websiteUrl: string
  /** The LinkedIn company page address. */
  readonly linkedInUrl: string
}

/** What the saved data holds, reduced to what the first screen turns on. */
export interface BrandFactsResumeState {
  /** True when a sheet is saved. */
  readonly hasSheet: boolean
  /** True while the latest read is still working. */
  readonly runLive: boolean
  /** True when the latest read failed, was cancelled or timed out. */
  readonly runFailed: boolean
}

/** The screen the step opens on, taken once from the saved data; a confirmed sheet opens on the sheet. */
export function resumeBrandFactsScreen(state: BrandFactsResumeState): BrandFactsScreen {
  if (state.runLive) return 'reading'
  if (state.runFailed && !state.hasSheet) return 'reading'
  return state.hasSheet ? 'sheet' : 'links'
}

/** The screen a move leads to. A move that does not apply to the screen on show keeps it. */
export function moveBrandFacts(from: BrandFactsScreen, move: BrandFactsMove): BrandFactsScreen {
  switch (move) {
    case 'keep-links':
      return from === 'links' ? 'sheet' : from
    case 'read-opened':
      return from === 'links' || from === 'sheet' ? 'reading' : from
    case 'read-landed':
      return from === 'reading' ? 'sheet' : from
    case 'retry':
      return from === 'reading' ? 'links' : from
  }
}

export const BRAND_FACTS_NAVIGATOR: SetupStepNavigator<BrandFactsScreen, BrandFactsMove> = {
  screens: BRAND_FACTS_SCREENS,
  move: moveBrandFacts,
}

/** Two typed addresses in the form they are compared in: normalised when they read as web addresses. */
function sameAddress(saved: string, typed: string): boolean {
  const comparable = (text: string): string => normalizeWebAddress(text) ?? text.trim()
  return comparable(saved) === comparable(typed)
}

/** True when the typed links match the saved sheet's links, so Next can skip the read. */
export function linksUnchanged(saved: BrandFactsLinks | null, typed: BrandFactsLinks): boolean {
  return (
    saved !== null &&
    sameAddress(saved.websiteUrl, typed.websiteUrl) &&
    sameAddress(saved.linkedInUrl, typed.linkedInUrl)
  )
}
